/**
 * The dashboard's routes for the two subjects, as plain hrefs.
 *
 * `@authe/tx-table` (and any other package that draws a subject) knows nothing
 * about this app's router, so a cell that wants to be a real anchor has to be
 * HANDED its destination. This is the one place that builds those, so every
 * table, panel and card points at the same address for the same subject.
 *
 * ## Why an href at all, when the click is already handled
 *
 * Because a click handler is not a link. Before this, the transactions table —
 * the surface the whole product opens on — rendered its hash and its from/to
 * addresses as `<button>`: no destination for cmd-click or middle-click to open,
 * no context menu ("open in new tab", "copy link address"), no URL in the status
 * bar on hover, and announced to a screen reader as a button rather than a link.
 * An analyst fanning a case across tabs had no way to do it from the list.
 *
 * The href does not take the click back. `opensInApp` (`@authe/ui-core`) keeps
 * the plain primary click for the app — so the side-panel preference, the graph
 * drill-down and SPA navigation all behave exactly as before — and lets every
 * modified click through to the browser, which is what the anchor is for.
 *
 * See {@link TransactionLink} in `SubjectLink.tsx`, which applies the same pair
 * (real anchor + intercepted plain click) to whole rows in this app's own JSX.
 */
import * as React from "react";
import { useRouter } from "@tanstack/react-router";
import type { TxTenancyScopeId } from "@authe/fe-commons/tenancy";

export interface SubjectHrefs {
  contractHref: (address: string, chain: string, context?: { block_number: number; tx_hash?: string }) => string;
  /**
   * The transaction's page, or `undefined` when the row cannot name one.
   *
   * Refs (`eth:0x…`) are accepted by `/transactions/$id`, but a row that has no
   * id at all — a pending ingest — has no page yet, and `undefined` is what
   * tells the cell to stay a button rather than render a link to nowhere.
   */
  txHref: (id: string | null | undefined, scope?: TxTenancyScopeId) => string | undefined;
  /** The address's page, scoped to a chain when the caller knows one. */
  addressHref: (address: string | null | undefined, chain?: string | null) => string | undefined;
}

export function useSubjectHrefs(): SubjectHrefs {
  const router = useRouter();
  return React.useMemo(
    () => ({
      contractHref: (address, chain, context) => router.buildLocation({
        to: "/contracts/$chainId/$address", params: { chainId: chain, address },
        search: { block: context?.block_number.toString(), tx: context?.tx_hash, tab: "onchain" },
      } as never).href,
      txHref: (id, scope) => {
        if (!id) return undefined;
        return router.buildLocation({ to: "/transactions/$id", params: { id }, ...(scope ? { search: { scope } } : {}) }).href;
      },
      addressHref: (address, chain) => {
        if (!address) return undefined;
        return router.buildLocation({
          to: "/addresses/$id",
          params: { id: address },
          ...(chain ? { search: { chain } } : {}),
        } as never).href;
      },
    }),
    [router],
  );
}

/**
 * The transaction id a row can be opened by, preferring the canonical one.
 *
 * A row carries both a `canonical_tx_id` and a `tx_hash`, and they are not
 * interchangeable: the canonical id names exactly one transaction, while a bare
 * hash names up to one on EVERY chain. So the link uses the canonical id when
 * the row has one and falls back to the chain-qualified ref, which
 * `/transactions/$id` parses — never the bare hash on its own, which would open
 * the "which chain did you mean" probe for a row that already knows.
 *
 * See `api/transactionRef.ts` and the `route-params-are-refs-not-ids` rule: a
 * canonical id is a bare 64-hex digest, anything with `:` or `0x` is a ref.
 */
export function txRouteIdOf(row: {
  canonical_tx_id?: string | null;
  tx_hash?: string | null;
  chain_id?: string | null;
}): string | undefined {
  if (row.canonical_tx_id) return row.canonical_tx_id;
  const hash = row.tx_hash;
  if (!hash) return undefined;
  return row.chain_id ? `${row.chain_id}:${hash}` : hash;
}
